import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    input,
    model,
    OnDestroy,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { AiTelemetry } from "../../services/ai.telemetry";
import { FormsModule } from "@angular/forms";
import { SimpleMarkdownPipe } from "../../pipes/s25.pipes";
import type { TelemetrySession } from "../../services/telemetry/telemetry.session";
import { ChatMessage, BuildResult, FormEditDiff, PlannedForm } from "./form.workflow.plan";
import { FormWorkflowBuilderService } from "./form.workflow.builder.service";
import { CacheRepository } from "../../decorators/cache.decorator";
import { AlertService } from "../../services/alert.service";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";

@Component({
    selector: "s25-ng-ai-form-editor",
    standalone: true,
    imports: [FormsModule, SimpleMarkdownPipe, S25IconComponent],
    host: { class: "ai-form-editor" },
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="ai-form-editor__chat">
            <div class="ai-form-editor__messages" #messagesContainer>
                @for (msg of messages(); track $index) {
                    <div
                        class="ai-form-editor__message"
                        [class.ai-form-editor__message--user]="msg.role === 'user'"
                        [class.ai-form-editor__message--assistant]="msg.role === 'assistant'"
                    >
                        <strong class="ai-form-editor__role">{{ msg.role === "user" ? "You" : "AI" }}:</strong>
                        <span [innerHTML]="msg.content | markdown"></span>
                    </div>
                }
                @if (isPlanning()) {
                    <div class="ai-form-editor__message ai-form-editor__message--assistant ai-form-editor__thinking">
                        <div class="ai-form-editor__dots"><span></span><span></span><span></span></div>
                        <em>{{ planningStatus() }}</em>
                    </div>
                }
            </div>
            <div class="ai-form-editor__input">
                <textarea
                    [(ngModel)]="userInput"
                    placeholder="Describe the changes you want to make to this form..."
                    rows="3"
                    (keydown.enter)="$event.shiftKey ? null : onSend($event)"
                ></textarea>
                @if (inputTooLong()) {
                    <div class="ai-form-editor__char-warning">
                        Message exceeds the {{ maxMessageLength }} character limit.
                    </div>
                }
                <div class="ai-form-editor__input-buttons">
                    <button class="aw-button aw-button--outline" (click)="onExit()">Exit</button>
                    <button
                        class="aw-button aw-button--primary"
                        (click)="onSend()"
                        [disabled]="!userInput().trim() || isPlanning() || isBuilding() || inputTooLong()"
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>

        <div class="ai-form-editor__preview">
            <h3 class="ai-assisted-heading">
                <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" class="ai-sparkles-adjacent" />
                Change Preview
            </h3>
            @if (currentDiff()) {
                @if (!hasPlanChanges()) {
                    <p class="c-textMuted">
                        No changes detected. The plan matches the current form — the AI may not have applied your edits.
                        Try rephrasing or send again.
                    </p>
                } @else {
                    @if (currentDiff()!.addedFields?.length) {
                        <h5>Added fields</h5>
                        <ul>
                            @for (field of currentDiff()!.addedFields; track field) {
                                <li class="ai-form-editor__added">{{ field }}</li>
                            }
                        </ul>
                    }
                    @if (currentDiff()!.removedFields?.length) {
                        <h5>Removed fields</h5>
                        <ul>
                            @for (field of currentDiff()!.removedFields; track field) {
                                <li class="ai-form-editor__removed">{{ field }}</li>
                            }
                        </ul>
                    }
                    @if (currentDiff()!.updatedFields?.length) {
                        <h5>Updated fields</h5>
                        <ul>
                            @for (field of currentDiff()!.updatedFields; track field) {
                                <li class="ai-form-editor__updated">{{ field }}</li>
                            }
                        </ul>
                    }
                    @if (currentDiff()!.ruleChanges?.length) {
                        <h5>Rule changes</h5>
                        <ul>
                            @for (change of currentDiff()!.ruleChanges; track change) {
                                <li>{{ change }}</li>
                            }
                        </ul>
                    }
                    @if (currentDiff()!.scopeChanges?.length) {
                        <h5>Scope</h5>
                        <ul>
                            @for (change of currentDiff()!.scopeChanges; track change) {
                                <li>{{ change }}</li>
                            }
                        </ul>
                    }
                }

                <div class="ai-form-editor__build-section">
                    <button
                        class="aw-button aw-button--primary"
                        (click)="onBuild()"
                        [disabled]="isBuilding() || isPlanning() || !currentPlan() || !hasPlanChanges()"
                    >
                        {{ isBuilding() ? "Building..." : "Build" }}
                    </button>
                </div>
            } @else {
                <p class="c-textMuted">No plan yet. Describe what you want to change and send a message.</p>
            }

            @if (buildResult()) {
                <div
                    class="ai-form-editor__result"
                    [class.ai-form-editor__result--warnings]="buildResult()!.warnings?.length"
                >
                    <h4>{{ buildResult()!.warnings?.length ? "Build completed with warnings" : "Build Result" }}</h4>
                    @if (buildResult()!.ingestionPointId) {
                        <p>Form ID: {{ buildResult()!.ingestionPointId }}</p>
                    }
                    @if (buildResult()!.warnings?.length) {
                        <div class="ai-form-editor__warnings">
                            <h5>Warnings:</h5>
                            <ul>
                                @for (warning of buildResult()!.warnings; track warning) {
                                    <li>{{ warning }}</li>
                                }
                            </ul>
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        .ai-assisted-heading {
            display: flex;
            align-items: center;
            gap: 0.35em;
        }

        .ai-sparkles-adjacent {
            flex-shrink: 0;
        }

        :host.ai-form-editor {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            min-height: 500px;
        }
        .ai-form-editor__chat {
            display: flex;
            flex-direction: column;
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }
        .ai-form-editor__messages {
            flex: 1;
            overflow-y: auto;
            padding: 16px;
            max-height: 400px;
        }
        .ai-form-editor__message {
            margin-bottom: 12px;
            padding: 8px 12px;
            border-radius: 8px;
        }
        .ai-form-editor__message--user {
            background: color-mix(in srgb, var(--cn-blue) 15%, var(--color--surface-bg));
            text-align: right;
        }
        .ai-form-editor__message--assistant {
            background: var(--color--surface-2-bg);
        }
        .ai-form-editor__role {
            margin-right: 6px;
        }
        .ai-form-editor__input {
            border-top: 1px solid var(--color--border);
            padding: 12px;
        }
        .ai-form-editor__input textarea {
            width: 100%;
            resize: vertical;
            margin-bottom: 8px;
        }
        .ai-form-editor__input-buttons {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
        }
        .ai-form-editor__preview {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 16px;
            overflow-y: auto;
            max-height: 550px;
        }
        .ai-form-editor__build-section {
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid var(--color--border);
        }
        .ai-form-editor__result {
            margin-top: 16px;
            padding: 12px;
            background: color-mix(in srgb, var(--color--objects-event) 18%, var(--color--surface-bg));
            border-radius: 8px;
        }
        .ai-form-editor__result--warnings {
            background: color-mix(in srgb, var(--color--text-warning) 18%, var(--color--surface-bg));
        }
        .ai-form-editor__warnings {
            color: var(--color--text-warning);
        }
        .ai-form-editor__added {
            color: var(--color--objects-event);
        }
        .ai-form-editor__removed {
            color: var(--color--danger);
        }
        .ai-form-editor__updated {
            color: var(--color--link-text);
        }
        .ai-form-editor__char-warning {
            color: var(--color--danger);
            font-size: 0.85em;
            margin-bottom: 4px;
        }
        .ai-form-editor__thinking {
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .ai-form-editor__dots {
            display: flex;
            gap: 4px;
        }
        .ai-form-editor__dots span {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: var(--color--text-muted);
            animation: ai-form-editor-dot-bounce 1.4s ease-in-out infinite;
        }
        .ai-form-editor__dots span:nth-child(2) {
            animation-delay: 0.2s;
        }
        .ai-form-editor__dots span:nth-child(3) {
            animation-delay: 0.4s;
        }
        @keyframes ai-form-editor-dot-bounce {
            0%,
            80%,
            100% {
                transform: scale(0.6);
                opacity: 0.4;
            }
            40% {
                transform: scale(1);
                opacity: 1;
            }
        }
    `,
})
export class AiFormEditorComponent implements OnDestroy {
    static readonly MAX_MESSAGE_LENGTH = 4_000;

    private static readonly PLANNING_MESSAGES = [
        "Analyzing your request...",
        "Loading current form...",
        "Reviewing existing fields...",
        "Reviewing existing rules...",
        "Applying your changes...",
        "Validating the plan...",
        "Almost there...",
        "Finalizing the plan...",
    ];

    ingestionPointId = input.required<number>();
    exit = output<void>();
    buildComplete = output<void>();

    messages = signal<ChatMessage[]>([]);
    currentPlan = signal<PlannedForm | null>(null);
    currentDiff = signal<FormEditDiff | null>(null);
    buildResult = signal<BuildResult | null>(null);
    isPlanning = signal(false);
    isBuilding = signal(false);
    planningStatus = signal("Analyzing your request...");
    maxMessageLength = AiFormEditorComponent.MAX_MESSAGE_LENGTH;
    userInput = model("");
    readonly inputTooLong = computed(() => this.userInput().length > AiFormEditorComponent.MAX_MESSAGE_LENGTH);
    readonly hasPlanChanges = computed(() => {
        const diff = this.currentDiff();
        if (!diff) {
            return false;
        }
        return !!(
            diff.addedFields?.length ||
            diff.removedFields?.length ||
            diff.updatedFields?.length ||
            diff.ruleChanges?.length ||
            diff.scopeChanges?.length
        );
    });
    private messagesContainer = viewChild<ElementRef<HTMLElement>>("messagesContainer");
    private planningTimer: ReturnType<typeof setInterval> | null = null;
    private readonly aiSession: TelemetrySession = AiTelemetry.createSession("FormEditor");

    constructor() {
        this.aiSession.open();
        effect(() => {
            this.messages();
            this.isPlanning();
            setTimeout(() => {
                const element = this.messagesContainer()?.nativeElement;
                if (element) {
                    element.scrollTop = element.scrollHeight;
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.aiSession.close();
    }

    onExit(): void {
        this.exit.emit();
    }

    async onSend(event?: Event) {
        event?.preventDefault();
        const text = this.userInput().trim();
        if (!text || this.isPlanning() || this.isBuilding()) {
            return;
        }

        this.aiSession.engage();
        this.userInput.set("");
        this.messages.update((msgs) => [...msgs, { role: "user", content: text }]);
        this.isPlanning.set(true);
        this.startPlanningTimer();

        try {
            const allMessages = this.messages().map((message) => ({ role: message.role, content: message.content }));
            const response = await FormWorkflowBuilderService.editPlan(this.ingestionPointId(), allMessages);
            this.currentPlan.set(response.plan ?? null);
            this.currentDiff.set(response.diff ?? null);
            const applied = !!response.plan && this.hasPlanChanges();
            const fallback = applied
                ? "These changes are not saved yet. Review the preview and click Build to apply them."
                : "I wasn't able to apply your requested changes. Try rephrasing or send again.";
            const chatResponse = response.chatResponse ?? "";
            const hasServerFailure =
                chatResponse.includes("Remaining issues") || chatResponse.includes("No changes applied");
            const content =
                applied || hasServerFailure
                    ? AiFormEditorComponent.cleanChatResponse(chatResponse, fallback)
                    : fallback;
            this.messages.update((msgs) => [...msgs, { role: "assistant", content }]);
        } catch (caughtError: any) {
            this.messages.update((msgs) => [
                ...msgs,
                { role: "assistant", content: "Error: " + AiFormEditorComponent.extractErrorMessage(caughtError) },
            ]);
        } finally {
            this.stopPlanningTimer();
            this.isPlanning.set(false);
        }
    }

    async onBuild() {
        const plan = this.currentPlan();
        if (!plan || this.isBuilding()) {
            return;
        }

        this.isBuilding.set(true);
        this.buildResult.set(null);

        try {
            const result = await FormWorkflowBuilderService.editBuild(this.ingestionPointId(), plan);
            CacheRepository.invalidateByService("RuleTreeService");
            CacheRepository.invalidateByService("DataIngestionService");
            this.buildResult.set(result);
            if (result.warnings?.length) {
                await AlertService.alert(result.warnings.join("\n\n"));
            }
            if (result.ingestionPointId) {
                this.aiSession.complete();
                this.buildComplete.emit();
            }
        } catch (caughtError: any) {
            this.buildResult.set({
                ingestionPointId: null,
                workflowProcessId: null,
                warnings: ["Build failed: " + AiFormEditorComponent.extractErrorMessage(caughtError)],
            });
        } finally {
            this.isBuilding.set(false);
        }
    }

    private startPlanningTimer() {
        let step = 0;
        this.planningStatus.set(AiFormEditorComponent.PLANNING_MESSAGES[0]);
        this.planningTimer = setInterval(() => {
            step = Math.min(step + 1, AiFormEditorComponent.PLANNING_MESSAGES.length - 1);
            this.planningStatus.set(AiFormEditorComponent.PLANNING_MESSAGES[step]);
        }, 8_000);
    }

    private stopPlanningTimer() {
        if (this.planningTimer) {
            clearInterval(this.planningTimer);
            this.planningTimer = null;
        }
    }

    private static cleanChatResponse(raw: string | null | undefined, fallback: string): string {
        if (!raw) {
            return fallback;
        }
        const text = raw
            .replace(/`{1,}[\s]*json?\s*/gi, "")
            .replace(/`{1,}\s*/g, "")
            .replace(/\{[\s\S]*"form"\s*:[\s\S]*\}/g, "")
            .trim();
        return text || fallback;
    }

    private static extractErrorMessage(caughtError: any): string {
        const body = caughtError?.error || caughtError?.data;
        if (body?.warnings?.length) {
            return body.warnings.join("; ");
        }
        if (body?.chatResponse) {
            return body.chatResponse;
        }
        if (caughtError?.status === 403) {
            return "Workflow and Form Assistance is not enabled for this instance.";
        }
        if (caughtError?.status >= 500) {
            return "A server error occurred. Please try again.";
        }
        if (caughtError?.status > 0) {
            return "Request failed (status " + caughtError.status + "). Please try again.";
        }
        if (typeof body === "string") {
            return body;
        }
        return caughtError?.statusText || "Something went wrong. Please try again.";
    }
}
